/* RESPONSIBLE TEAM: team-billing */

import nonEuTaxEligibleCountries from '../lib/non-eu-tax-eligible-countries';

// Countries added in Wave 2 — gated by tax-collection-second-wave feature flag in UI,
// but always validated here so format checks work when the flag is enabled.
export const SECOND_WAVE_COUNTRIES = ['NG'];

const PLACEHOLDER_KEYS = {
  AU: 'signup.teams.pricing5.annual-plans.credit-card-details.au-tax-number-placeholder',
  CA: 'signup.teams.pricing5.annual-plans.credit-card-details.ca-tax-number-placeholder',
  CH: 'signup.teams.pricing5.annual-plans.credit-card-details.ch-tax-number-placeholder',
  LI: 'signup.teams.pricing5.annual-plans.credit-card-details.li-tax-number-placeholder',
  MY: 'signup.teams.pricing5.annual-plans.credit-card-details.my-tax-number-placeholder',
  NG: 'signup.teams.pricing5.annual-plans.credit-card-details.ng-tax-number-placeholder',
  NO: 'signup.teams.pricing5.annual-plans.credit-card-details.no-tax-number-placeholder',
  NZ: 'signup.teams.pricing5.annual-plans.credit-card-details.nz-tax-number-placeholder',
};

const DEFAULT_PLACEHOLDER_KEY =
  'signup.teams.pricing5.annual-plans.credit-card-details.default-tax-number-placeholder';

const ERROR_KEYS = {
  AU: 'signup.teams.pricing5.annual-plans.credit-card-details.au-tax-number-error',
  CA: 'signup.teams.pricing5.annual-plans.credit-card-details.ca-tax-number-error',
  CH: 'signup.teams.pricing5.annual-plans.credit-card-details.ch-tax-number-error',
  LI: 'signup.teams.pricing5.annual-plans.credit-card-details.li-tax-number-error',
  MY: 'signup.teams.pricing5.annual-plans.credit-card-details.my-tax-number-error',
  NG: 'signup.teams.pricing5.annual-plans.credit-card-details.ng-tax-number-error',
  NO: 'signup.teams.pricing5.annual-plans.credit-card-details.no-tax-number-error',
  NZ: 'signup.teams.pricing5.annual-plans.credit-card-details.nz-tax-number-error',
};

const DEFAULT_ERROR_KEY =
  'signup.teams.pricing5.annual-plans.credit-card-details.default-tax-number-error';

/**
 * Validates VAT number based on country-specific rules derived from placeholder patterns
 * @param {string} vatNumber - The VAT number to validate
 * @param {string} countryCode - The country code (e.g., 'AU', 'CA', 'CH')
 * @returns {boolean} - True if VAT number is valid for the country
 */
export function validateVatNumber(vatNumber, countryCode) {
  if (!vatNumber || !countryCode) {
    return true; // VAT number is optional, so empty is valid
  }

  // If not a non-EU tax eligible country (or wave 2 country), skip validation (always valid)
  if (
    !nonEuTaxEligibleCountries.includes(countryCode) &&
    !SECOND_WAVE_COUNTRIES.includes(countryCode)
  ) {
    return true;
  }

  // Remove spaces and normalize
  let normalizedVat = vatNumber.replace(/\s/g, '');

  // Country-specific validation rules based on placeholder patterns
  switch (countryCode) {
    case 'AU': // Australia: 11 digits
      return /^\d{11}$/.test(normalizedVat);

    case 'CA': // Canada: 9 digits + RT + 4 digits (123456789RT0001)
      return /^\d{9}RT\d{4}$/.test(normalizedVat);

    case 'CH': // Switzerland: CHE + 9 digits + MWST, with dots (CHE123.456.789MWST)
      return /^CHE\d{3}\.\d{3}\.\d{3}MWST$/.test(normalizedVat);

    case 'LI': // Liechtenstein: 5 digits
      return /^\d{5}$/.test(normalizedVat);

    case 'MY': // Malaysia: Letter + 14 digits with hyphens (A12-3456-78912345)
      return /^[A-Z]\d{2}-\d{4}-\d{8}$/.test(normalizedVat);

    case 'NG': // Nigeria: 8 digits + hyphen + 4 digits (12345678-0001)
      return /^\d{8}-\d{4}$/.test(normalizedVat);

    case 'NO': // Norway: 9 digits + MVA (123456789MVA)
      return /^\d{9}MVA$/.test(normalizedVat);

    case 'NZ': // New Zealand: 9 digits
      return /^\d{9}$/.test(normalizedVat);

    default:
      // For any other countries, skip validation
      return true;
  }
}

/**
 * Gets the translation key for validation error message for invalid VAT numbers
 * @param {string} countryCode - The country code
 * @returns {string} - Translation key for the error message
 */
export function getVatValidationErrorKey(countryCode) {
  if (!countryCode) {
    return DEFAULT_ERROR_KEY;
  }
  return ERROR_KEYS[countryCode] ?? DEFAULT_ERROR_KEY;
}

/**
 * Gets the translation key for the placeholder text shown in tax ID number inputs.
 * Used by both address-form.js (billing settings) and vat-field.ts (Stripe checkout)
 * so that adding a new country only requires updating this file.
 * @param {string} countryCode - The country code
 * @returns {string} - Translation key for the placeholder
 */
export function getVatPlaceholderKey(countryCode) {
  if (!countryCode) {
    return DEFAULT_PLACEHOLDER_KEY;
  }
  return PLACEHOLDER_KEYS[countryCode] ?? DEFAULT_PLACEHOLDER_KEY;
}
